import cx from "classnames";

import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import Box from "ds/components/Box";
import TileWrapper from "ds/components/Tile/Wrapper";
import TileContent from "ds/components/Tile/Content";
import TileTitle from "ds/components/Tile/Title";
import IconEmphasized from "ds/components/IconEmphasized";
import { File, ThumbUp } from "components/icons/generated";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageLaunchpad } from "hooks/useAnalytics/pages/launchpad";
import useTitle from "hooks/useTitle";
import FeedbackButton from "components/FeedbackButton";
import { getDocsUrl } from "utils/getDocsUrl";
import { isSelfHostedDistribution } from "utils/distribution";
import ActionCard from "ds/components/ActionCard";
import useIsLearnViewAvailable from "hooks/useIsLearnViewAvailable";
import useIsOnboardingCertificatesAvailable from "hooks/useIsOnboardingCertificatesAvailable";
import Tab from "ds/components/Tab";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import styles from "./styles.module.css";
import LaunchPadKnowledgeBase from "./KnowledgeBase";
import LaunchPadFAQ from "./FAQ";
import LaunchPadVideos from "./Videos";
import LaunchPadExploreNext from "./ExploreNext";
import LaunchPadChecklistSaaS from "./ChecklistSaaS";
import LaunchPadChecklistSelfHosted from "./ChecklistSelfHosted";
import LaunchPadLearn from "./Learn";

const isSelfHosted = isSelfHostedDistribution();

const LaunchPadBoard = () => {
  useTitle("LaunchPad");

  const { viewer } = useTypedContext(AccountContext);
  const isOnboardingCertificatesAvailable = useIsOnboardingCertificatesAvailable();

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageLaunchpad.Launchpad,
  });

  const goToDocs = () => {
    trackSegmentAnalyticsEvent("Docs Click");
    window.open(getDocsUrl(), "_blank");
  };

  const isNewLearnVisible = useIsLearnViewAvailable();

  return (
    <>
      <ViewHeader>
        <ViewHeaderTitle>
          <Box align="center" gap="medium">
            Welcome to LaunchPad
          </Box>
        </ViewHeaderTitle>
        {viewer.admin && isOnboardingCertificatesAvailable && (
          <ViewHeaderNavigation>
            <Tab to="/launchpad" end label="Learn" />
            <Tab to="/launchpad/track" label="Track" />
          </ViewHeaderNavigation>
        )}
      </ViewHeader>
      <Box grid gridTemplate={isNewLearnVisible ? "1fr" : "3.2fr minmax(28.1rem, 1fr)"} fullWidth>
        <Box
          className={cx(styles.mainContainer, isNewLearnVisible && styles.fullWidthContainer)}
          direction="column"
          gap="x-large"
        >
          {isNewLearnVisible && <LaunchPadLearn />}
          {!isNewLearnVisible && (
            <>
              {isSelfHosted ? <LaunchPadChecklistSelfHosted /> : <LaunchPadChecklistSaaS />}
              <LaunchPadExploreNext />
            </>
          )}

          <LaunchPadKnowledgeBase />
          <LaunchPadVideos />
          <LaunchPadFAQ />
        </Box>
        {!isNewLearnVisible && (
          <Box
            className={styles.sideContainer}
            padding="large"
            gap="large"
            direction="column"
            justify="start"
            align="center"
          >
            <ActionCard
              iconColor="surface"
              emphasized
              title="Feedback"
              icon={ThumbUp}
              description="Help us craft experience based on your needs by providing feedback."
              actionComponent={<FeedbackButton id="pendo-launchpad-feedback" />}
            />
            <Box fullWidth>
              <TileWrapper onClick={goToDocs}>
                <TileContent direction="row" align="center">
                  <IconEmphasized icon={File} />
                  <TileTitle>See full documentation</TileTitle>
                </TileContent>
              </TileWrapper>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default LaunchPadBoard;
